import { bbox, bboxPolygon, buffer } from "@turf/turf"
import { colorToRgba } from "@ciq/shared/utils/color"
import { DEFAULT_METRIC_OUTLINE_COLOR } from "./colors"

// Fan pattern spacing between floor zones in meters
const FLOOR_FAN_VERTICAL_PADDING = 40
const FLOOR_FAN_HORIZONTAL_PADDING = 20
// Convert meters to approximate coordinate offset (rough approximation: 1 meter ≈ 0.000009 degrees)
const METERS_TO_DEGREES = 0.000009

/**
 * Creates a fan pattern layout for floor zones positioned below a building.
 * Transforms floor and end zone geometries to be arranged horizontally with spacing.
 *
 * @param {Object} params - Parameters for fan pattern generation
 * @param {Object} params.feature - The building feature (parent zone)
 * @param {Array} params.floors - Array of floor objects with floor feature and children
 * @returns {Array|null} Array of transformed GeoJSON features or null if invalid
 */
export const createFloorFanPattern = ({ feature, floors }) => {
  // Get parent bounds for positioning
  const parentBbox = bbox(feature)
  if (!parentBbox) {
    return null
  }

  const fanFeatures = []
  const geoJsonLookup = { [feature.properties.id]: feature }

  for (const floorValue of floors) {
    geoJsonLookup[floorValue.floor.properties.id] = floorValue.floor
    for (const childFeature of floorValue.children) {
      geoJsonLookup[childFeature.properties.id] = childFeature
    }
  }

  const parentBounds = {
    minLng: parentBbox[0],
    minLat: parentBbox[1],
    maxLng: parentBbox[2],
    maxLat: parentBbox[3],
    width: parentBbox[2] - parentBbox[0],
    height: parentBbox[3] - parentBbox[1],
  }

  const floorFeatures = []
  const floorLookup = {}

  for (const value of floors) {
    floorFeatures.push(value.floor)
    floorLookup[value.floor.properties.floor] = value.children
  }

  // Sort floors by floor number
  const sortedFloors = floorFeatures.sort((a, b) => {
    const aFloor = a.properties.floor ? parseInt(a.properties.floor) : 0
    const bFloor = b.properties.floor ? parseInt(b.properties.floor) : 0
    return aFloor - bFloor
  })

  // Calculate total width needed for all floors with padding
  // Pattern: |zone|padding|zone|padding|zone|
  let totalWidth = 0
  const floorWidths = []

  for (const floor of sortedFloors) {
    const floorBbox = bbox(floor)
    if (floorBbox) {
      const floorWidth = floorBbox[2] - floorBbox[0] // maxLng - minLng
      floorWidths.push(floorWidth)
      totalWidth += floorWidth
    } else {
      floorWidths.push(0)
    }
  }

  // Add padding between floors (n-1 padding spaces for n floors)
  const totalPadding = (sortedFloors.length - 1) * FLOOR_FAN_HORIZONTAL_PADDING * METERS_TO_DEGREES
  totalWidth += totalPadding

  // Calculate starting position (center the entire fan pattern)
  const startLng = parentBounds.minLng + (parentBounds.width - totalWidth) / 2

  // Calculate target position for floor zones: top of floor should be building bottom - padding
  const floorTopTarget = parentBounds.minLat - FLOOR_FAN_VERTICAL_PADDING * METERS_TO_DEGREES

  let currentLng = startLng
  sortedFloors.forEach((floor, index) => {
    const floorWidth = floorWidths[index]

    // Get floor zone bounds to calculate proper positioning
    const floorBbox = bbox(floor)
    if (!floorBbox) return

    const floorCurrentTop = floorBbox[3] // current maxLat of floor
    const floorCurrentLeft = floorBbox[0] // current minLng of floor

    // Calculate offset so that top of floor zone aligns with target position
    const fanVerticalOffset = floorTopTarget - floorCurrentTop

    // Calculate horizontal offset so that left edge of floor aligns with currentLng
    const fanHorizontalOffset = currentLng - floorCurrentLeft

    // Position floor at current horizontal position
    const offset = {
      lng: fanHorizontalOffset,
      lat: fanVerticalOffset,
    }

    // Create fan version with transformed geometry
    const geoJSON = {
      ...floor,
      geometry: transformGeometryForFan(floor.geometry, offset),
    }

    fanFeatures.push(geoJSON)

    // Add fan floor to the updated lookup with a unique ID for the fan version
    geoJsonLookup[geoJSON.properties.id] = geoJSON

    // Get and transform sub zones for this floor
    const transformedSubZones = createTransformedSubZones(
      geoJSON,
      offset,
      floorLookup[floor.properties.floor],
      geoJsonLookup
    )
    fanFeatures.push(...transformedSubZones)

    // Move to next position: current floor width + padding
    currentLng += floorWidth + FLOOR_FAN_HORIZONTAL_PADDING * METERS_TO_DEGREES
  })

  if (fanFeatures.length) {
    const boundingBox = createFanBoundingBox(fanFeatures, feature.properties.buildingId)
    if (boundingBox) {
      fanFeatures.push(boundingBox)
    }
  }

  return fanFeatures
}

/**
 * Creates a bounding box polygon around fan pattern features.
 * Provides visual separation between the fan layout and the regular map.
 *
 * @param {Array} features - Array of fan pattern features
 * @param {string} buildingId - Building ID for the bbox feature
 * @returns {Object|null} Buffered bounding box polygon or null if invalid
 */
const createFanBoundingBox = (features, buildingId) => {
  if (!features?.length) {
    return null
  }

  const box = bbox({ type: "FeatureCollection", features })
  if (!box) {
    return null
  }

  const feature = bboxPolygon(box, {
    properties: {
      id: `bbox-${buildingId}`,
      buildingId: buildingId,
      "fill-color": colorToRgba("#fff", 1),
      "outline-color": colorToRgba(DEFAULT_METRIC_OUTLINE_COLOR, 1),
      opacity: 1,
      viewType: "bbox",
    },
  })

  return buffer(feature, 30, { units: "meters" })
}

/**
 * Transforms geometry coordinates by applying an offset.
 * Recursively handles nested coordinate arrays (polygon rings).
 *
 * @param {Object} geometry - GeoJSON geometry object
 * @param {Object} offset - Offset to apply { lng, lat }
 * @returns {Object} Transformed geometry object
 */
const transformGeometryForFan = (geometry, offset) => {
  if (!geometry || !geometry.coordinates) {
    return geometry
  }

  const transformCoordinate = coord => {
    if (Array.isArray(coord[0])) {
      // Nested array (polygon rings)
      return coord.map(transformCoordinate)
    }
    // Individual coordinate [lng, lat]
    return [coord[0] + offset.lng, coord[1] + offset.lat]
  }

  return {
    ...geometry,
    coordinates: transformCoordinate(geometry.coordinates),
  }
}

/**
 * Creates transformed sub zones (end zones) for a floor zone.
 * Applies the same coordinate offset to all children.
 *
 * @param {Object} floor - The transformed floor feature
 * @param {Object} offset - Offset to apply to children { lng, lat }
 * @param {Array} children - Array of child zone features
 * @param {Object} geojsonLookup - Lookup object to store transformed features
 * @returns {Array} Array of transformed child features
 */
const createTransformedSubZones = (floor, offset, children, geojsonLookup) => {
  const result = []

  if (!children?.length) {
    return result
  }

  for (const geoJSON of children) {
    const fanSubZone = {
      ...geoJSON,
      geometry: transformGeometryForFan(geoJSON.geometry, offset),
    }

    result.push(fanSubZone)
    geojsonLookup[fanSubZone.properties.id] = fanSubZone
  }

  return result
}
